/**
 * Generate a URL-friendly slug from text
 * @param {string} text - Text to slugify
 * @returns {string} URL-friendly slug
 */
const slugify = (text) => (text || '')
  .toString()
  .toLowerCase()
  .trim()
  .replace(/[^a-z0-9]+/g, '-') // Replace non-alphanumeric with hyphens
  .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens

const DETAIL_SEGMENT = 'dp';

const normalizeId = (id) => (id === null || id === undefined ? '' : id.toString().trim());

const normalizeSuffix = (suffix) => {
  if (!suffix) return '';
  return `/${suffix.toString().replace(/^\/+/, '')}`;
};

export const buildDetailPath = (basePath, nameSlug, id, suffix = '') => {
  const normalizedId = normalizeId(id);
  const slug = nameSlug || 'item';
  return `${basePath}/${slug}/${DETAIL_SEGMENT}/${normalizedId}${normalizeSuffix(suffix)}`;
};

export const buildShortDetailPath = (basePath, id, suffix = '') => (
  `${basePath}/${DETAIL_SEGMENT}/${normalizeId(id)}${normalizeSuffix(suffix)}`
);

export const parseLegacyCombinedSlug = (combined) => {
  if (!combined) return { nameSlug: null, id: null };

  const value = combined.toString().trim();
  if (/^\d+$/.test(value)) return { nameSlug: null, id: value };

  const [, id] = value.match(/-(\d+)$/) || [];
  if (!id) return { nameSlug: slugify(value), id: null };

  const nameSlug = value
    .slice(0, -(id.length + 1))
    .replace(/^-+|-+$/g, '');

  return { nameSlug: nameSlug || null, id };
};

/**
 * Generate a URL-friendly slug from an influencer's name
 * @param {string} fullName - Influencer's full name
 * @returns {string} URL-friendly slug (e.g., "john-smith")
 */
export const generateInfluencerSlug = (fullName) => slugify(fullName);

/**
 * Generate a full influencer profile URL
 * @param {number|string} id - Influencer's ID
 * @param {string} fullName - Influencer's full name
 * @returns {string} Full URL path (e.g., /marketplace/influencers/john-smith-123)
 */
export const getInfluencerProfileUrl = (id, fullName) => {
  const slug = generateInfluencerSlug(fullName);
  return buildDetailPath('/marketplace/influencers', slug, id);
};

/**
 * Generate a URL-friendly slug from a campaign name
 * @param {string} campaignName - Campaign name
 * @returns {string} URL-friendly slug
 */
export const generateCampaignSlug = (campaignName) => slugify(campaignName);

/**
 * Generate a full campaign detail URL for brands
 * @param {number|string} id - Campaign ID
 * @param {string} campaignName - Campaign name
 * @returns {string} Full URL path (e.g., /dashboard/campaigns/summer-collection-456)
 */
export const getCampaignDetailUrl = (id, campaignName) => {
  const slug = generateCampaignSlug(campaignName);
  return buildDetailPath('/dashboard/campaigns', slug, id);
};

/**
 * Generate a brand campaign edit URL
 * @param {number|string} id - Campaign ID
 * @param {string} campaignName - Campaign name
 * @returns {string} Full URL path (e.g., /dashboard/campaigns/edit/summer-collection/dp/456)
 */
export const getCampaignEditUrl = (id, campaignName) => {
  const slug = generateCampaignSlug(campaignName);
  if (slug) return buildDetailPath('/dashboard/campaigns/edit', slug, id);
  return buildShortDetailPath('/dashboard/campaigns/edit', id);
};

/**
 * Generate a full marketplace campaign detail URL for influencers
 * @param {number|string} id - Campaign ID
 * @param {string} campaignName - Campaign name
 * @returns {string} Full URL path (e.g., /marketplace/campaigns/summer-collection-456)
 */
export const getMarketplaceCampaignDetailUrl = (id, campaignName) => {
  const slug = generateCampaignSlug(campaignName);
  return buildDetailPath('/marketplace/campaigns', slug, id);
};

/**
 * Generate a URL-friendly slug from a brand name
 * @param {string} brandName - Brand name
 * @returns {string} URL-friendly slug
 */
export const generateBrandSlug = (brandName) => slugify(brandName);

/**
 * Generate a full brand detail URL
 * @param {number|string} id - Brand ID
 * @param {string} brandName - Brand name
 * @returns {string} Full URL path (e.g., /marketplace/brands/nike-789)
 */
export const getBrandDetailUrl = (id, brandName) => {
  const slug = generateBrandSlug(brandName);
  return buildDetailPath('/marketplace/brands', slug, id);
};

/**
 * Generate a brand influencer details URL (for brand dashboard)
 * @param {number|string} influencerId - Influencer ID
 * @param {string} fullName - Influencer's full name
 * @param {number|string} campaignId - Campaign ID
 * @param {number|string} [inviteId] - Invite ID (required to distinguish repeat collaborations)
 * @returns {string} Full URL path
 */
export const getBrandInfluencerDetailsUrl = (influencerId, fullName, campaignId, inviteId) => {
  const slug = generateInfluencerSlug(fullName);
  const suffix = inviteId == null
    ? `campaign/${campaignId}`
    : `campaign/${campaignId}/invite/${inviteId}`;
  return buildDetailPath('/influencers', slug, influencerId, suffix);
};

/**
 * Generate a brand collaboration status URL (for brand dashboard)
 * @param {number|string} collaborationId - Collaboration ID
 * @param {string} influencerName - Influencer's name
 * @param {string} campaignName - Campaign name
 * @returns {string} Full URL path (e.g., /collaborations/john-smith-summer-campaign-123/status)
 */
export const getBrandCollaborationStatusUrl = (collaborationId, influencerName, campaignName) => {
  const influencerSlug = slugify(influencerName);
  const campaignSlug = slugify(campaignName);
  const slug = [influencerSlug, campaignSlug].filter(Boolean).join('-');
  return buildDetailPath('/collaborations', slug, collaborationId, 'status');
};

/**
 * Generate an influencer collaboration details URL (for influencer dashboard)
 * @param {number|string} collaborationId - Collaboration ID
 * @param {string} campaignName - Campaign name
 * @param {string} brandName - Brand name
 * @returns {string} Full URL path (e.g., /dashboard/collaborations/summer-campaign-nike-123)
 */
export const getInfluencerCollaborationDetailsUrl = (collaborationId, campaignName, brandName) => {
  const campaignSlug = slugify(campaignName);
  const brandSlug = slugify(brandName);
  const slug = [campaignSlug, brandSlug].filter(Boolean).join('-');
  return buildDetailPath('/dashboard/collaborations', slug, collaborationId);
};

/**
 * Generate an influencer invite collaboration status URL (for influencer dashboard)
 * @param {number|string} collaborationId - Collaboration ID
 * @param {string} campaignName - Campaign name
 * @param {string} brandName - Brand name
 * @returns {string} Full URL path (e.g., /dashboard/collaborations/summer-campaign-nike-123/invite-status)
 */
export const getInfluencerInviteCollaborationStatusUrl = (collaborationId, campaignName, brandName) => {
  const campaignSlug = slugify(campaignName);
  const brandSlug = slugify(brandName);
  const slug = [campaignSlug, brandSlug].filter(Boolean).join('-');
  return buildDetailPath('/dashboard/collaborations', slug, collaborationId, 'invite-status');
};

/**
 * Generate an influencer application details URL (for influencer dashboard)
 * @param {number|string} applicationId - Application ID
 * @param {string} campaignName - Campaign name
 * @param {string} brandName - Brand name
 * @returns {string} Full URL path (e.g., /dashboard/applications/summer-campaign-nike-123)
 */
export const getInfluencerApplicationDetailsUrl = (applicationId, campaignName, brandName) => {
  const campaignSlug = slugify(campaignName);
  const brandSlug = slugify(brandName);
  const slug = [campaignSlug, brandSlug].filter(Boolean).join('-');
  return buildDetailPath('/dashboard/applications', slug, applicationId);
};

/**
 * Generate a brand application details URL (for brand dashboard)
 * @param {number|string} applicationId - Application ID
 * @param {string} campaignName - Campaign name
 * @param {string} influencerName - Influencer name
 * @returns {string} Full URL path (e.g., /dashboard/applications/summer-campaign-john-smith-123)
 */
export const getBrandApplicationDetailsUrl = (applicationId, campaignName, influencerName) => {
  const campaignSlug = slugify(campaignName);
  const influencerSlug = slugify(influencerName);
  const slug = [campaignSlug, influencerSlug].filter(Boolean).join('-');
  return buildDetailPath('/dashboard/applications', slug, applicationId);
};

/**
 * Generate a unified collaboration flow URL (for both invites and applications)
 * @param {number|string} acceptanceId - Acceptance record ID
 * @param {string} [campaignName] - Campaign name
 * @param {string} [brandName] - Brand name
 * @returns {string} Full URL path (e.g., /dashboard/collaboration/summer-campaign-nike/dp/123)
 */
export const getCollaborationFlowUrl = (acceptanceId, campaignName, brandName) => {
  const campaignSlug = slugify(campaignName);
  const brandSlug = slugify(brandName);
  const slug = [campaignSlug, brandSlug].filter(Boolean).join('-');
  if (slug) return buildDetailPath('/dashboard/collaboration', slug, acceptanceId);
  return buildShortDetailPath('/dashboard/collaboration', acceptanceId);
};
